import { useEffect, useState } from "react";
import { useAuth } from "../contexts/authProvider";
import { trackEmailVerificationGateShown } from "../analytics/events";
import Icon from "./AppIcon";
import { useModal } from "hooks/useModal";

const RESEND_COOLDOWN = 60; // segundos entre reenvios

const EmailVerificationModal = () => {
  useModal(true, () => {}, { closeOnEscape: false, closeOnBack: false });

  const { user, resendEmailVerification, refreshEmailVerified, logout } =
    useAuth();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', message }
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  // Mede quantos usuários realmente batem nesse gate (funil de fricção).
  useEffect(() => {
    trackEmailVerificationGateShown();
  }, []);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setFeedback(null);
    try {
      await resendEmailVerification();
      setFeedback({
        type: "success",
        message:
          "Link de verificação reenviado! Confira sua caixa de entrada e a pasta de spam.",
      });
      startCooldown();
    } catch (error) {
      const tooMany = error?.code === "auth/too-many-requests";
      setFeedback({
        type: "error",
        message: tooMany
          ? "Muitas tentativas. Aguarde alguns minutos antes de reenviar."
          : "Não foi possível reenviar o e-mail. Tente novamente.",
      });
      if (tooMany) startCooldown();
    } finally {
      setSending(false);
    }
  };

  const handleCheck = async () => {
    if (checking) return;
    setChecking(true);
    setFeedback(null);
    try {
      const verified = await refreshEmailVerified();
      if (!verified) {
        setFeedback({
          type: "error",
          message:
            "Seu e-mail ainda não foi confirmado. Clique no link que enviamos e tente novamente.",
        });
      }
      // Se verificado, o gate desaparece automaticamente (o estado muda no contexto).
    } catch {
      setFeedback({
        type: "error",
        message: "Erro ao verificar. Tente novamente.",
      });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-4 text-center">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="MailCheck" size={28} className="text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">
            Confirme seu e-mail
          </h2>
          <p className="text-sm text-muted-foreground">
            Enviamos um link de verificação para{" "}
            <span className="font-medium text-foreground break-all">
              {user?.email}
            </span>
            . Confirme seu e-mail para liberar o acesso à plataforma.
          </p>

          {/* Aviso de spam sempre visível — o e-mail de verificação
              frequentemente cai em spam/promoções, e o usuário que não sabe
              disso desiste achando que nada chegou. */}
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-warning/10 border border-warning/20 p-3 text-left">
            <Icon
              name="Info"
              size={16}
              className="text-warning flex-shrink-0 mt-0.5"
            />
            <p className="text-xs text-muted-foreground">
              Não achou o e-mail? Verifique as pastas{" "}
              <span className="font-medium text-foreground">Spam</span> e{" "}
              <span className="font-medium text-foreground">Promoções</span>, e
              marque como &ldquo;não é spam&rdquo; para receber os próximos.
            </p>
          </div>
        </div>

        {/* Feedback */}
        {feedback && (
          <div
            className={`mx-6 mb-3 flex items-start space-x-2 p-3 rounded-lg border ${
              feedback.type === "success"
                ? "bg-success/10 border-success/20"
                : "bg-destructive/10 border-destructive/20"
            }`}
          >
            <Icon
              name={feedback.type === "success" ? "CheckCircle2" : "AlertCircle"}
              size={16}
              className={`flex-shrink-0 mt-0.5 ${
                feedback.type === "success"
                  ? "text-success"
                  : "text-destructive"
              }`}
            />
            <p
              className={`text-xs ${
                feedback.type === "success"
                  ? "text-success"
                  : "text-destructive"
              }`}
            >
              {feedback.message}
            </p>
          </div>
        )}

        {/* Ações */}
        <div className="px-6 pb-6 space-y-2">
          <button
            onClick={handleCheck}
            disabled={checking}
            className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-xl font-semibold transition-all hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {checking ? (
              <>
                <Icon name="Loader2" size={18} className="animate-spin" />
                Verificando...
              </>
            ) : (
              "Já confirmei meu e-mail"
            )}
          </button>

          <button
            onClick={handleResend}
            disabled={sending || cooldown > 0}
            className="w-full px-4 py-3 bg-background border border-border text-foreground rounded-xl font-medium transition-all hover:border-primary/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <Icon name="Loader2" size={18} className="animate-spin" />
                Reenviando...
              </>
            ) : cooldown > 0 ? (
              `Reenviar em ${cooldown}s`
            ) : (
              "Reenviar e-mail de verificação"
            )}
          </button>

          {confirmingLogout ? (
            <div className="pt-1 text-center">
              <p className="text-xs text-muted-foreground mb-2">
                Tem certeza? Você precisará fazer login novamente para voltar.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmingLogout(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-foreground border border-border rounded-xl hover:border-primary/50 transition-colors"
                >
                  Continuar aqui
                </button>
                <button
                  onClick={logout}
                  className="flex-1 px-4 py-2 text-sm text-muted-foreground hover:text-destructive transition-colors"
                >
                  Sair mesmo assim
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingLogout(true)}
              className="w-full px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Sair
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationModal;
